/**
 * KONFIGURACJA PRESETÓW HATCH CINEMATIC PER RARITY (sesja 29B).
 *
 * Każdy rarity ma własny wizualny "feel" w fazie burst + reveal:
 *  - Kolor flash/shockwave/particles
 *  - Liczba cząstek
 *  - Promień shockwave
 *  - Camera shake intensity + duration
 *  - Duration multiplier (legendary może trwać dłużej)
 *  - Liczba ringów shockwave (1 lub 2 — legendary/mythic dostają 2 dla efektu)
 *
 * **Huge** ma osobny override (`HUGE_HATCH_PRESET`) który zastępuje rarity preset
 * gdy `payload.isHuge === true || variant === 'huge'`. Huge jest bardzo specjalne
 * — większy scale, dłuższy reveal, biało-złoty (nie czerwony — Codex 23d feedback).
 *
 * **Particle texture keys:**
 *  - `fx-particle-grey`     — common (subtle)
 *  - `fx-particle-yellow`   — uncommon/rare/legendary (różne shading przez kolor flash)
 *  - `fx-particle-cyan`     — rare jako focal (lub rainbow base)
 *  - `fx-particle-white`    — generic sparkle (mythic accent)
 *
 * Wszystkie te tekstury są generowane proceduralnie w `PreloadScene.makeParticleTexture()`,
 * więc bez dodatkowych PNG-ów.
 *
 * **Pet Sim style:**
 *  - common = subtle, krótkie, bez shake'a
 *  - rare+ = wizualnie wyraźnie inne (Codex 23d preference: jakie pety dropię?
 *    Gracz musi widzieć rarity zanim przeczyta badge)
 *  - legendary/mythic = "wow moment" z 2 ringami, dużo particles, mocny shake
 *  - huge = osobny biało-złoty vibe, NIE czerwony horror
 */

import type { Rarity, Variant } from '@/domain/types/common';

export interface HatchRarityPreset {
  /** Główny kolor: flash + shockwave + glow. */
  primaryColor: number;
  /** Akcent na particles secondary (gdy oba kolory chcemy). */
  secondaryColor: number;
  /** Particle texture key z PreloadScene (proceduralne). */
  particleTextureKey: string;
  /** Liczba particles emitowanych w burst phase. */
  particleCount: number;
  /** Alpha glow początkowa (0-1). Legendary mocniejszy. */
  flashAlpha: number;
  /** Promień flash circle (px) — większy dla wyższych rarity. */
  flashRadius: number;
  /** Promień shockwave ring (px) — wzrost wizualny per rarity. */
  shockwaveRadius: number;
  /** Grubość shockwave line (px). */
  shockwaveLineWidth: number;
  /** Liczba ringów (1 dla większości, 2 dla legendary/mythic/huge, 3 dla titanic). */
  shockwaveRingCount: 1 | 2 | 3;
  /** Camera shake intensity (0 = brak, 0.004 = subtle, 0.015 = mocny). */
  cameraShakeIntensity: number;
  /** Camera shake duration (ms). */
  cameraShakeMs: number;
  /** Multiplier całkowitego czasu cinematic (1.0 = base ~2s, 1.2 = ~2.4s). */
  durationMult: number;
}

// ─────────────────────────────────────────────────────────────────────────────

export const HATCH_RARITY_PRESETS: Record<Rarity, HatchRarityPreset> = {
  // ── COMMON ────────────────────────────────────────────────────────────────
  // Subtle, krótkie. Bez camera shake — gracz nie poczuje haptic feedback dla
  // wszystkich common'ów (50% hatches z starter egga).
  common: {
    primaryColor: 0xCCCCCC,            // jasny szary
    secondaryColor: 0xFFFFFF,
    particleTextureKey: 'fx-particle-grey',
    particleCount: 8,
    flashAlpha: 0.7,
    flashRadius: 180,
    shockwaveRadius: 160,
    shockwaveLineWidth: 4,
    shockwaveRingCount: 1,
    cameraShakeIntensity: 0,
    cameraShakeMs: 0,
    durationMult: 1.0,
  },

  // ── UNCOMMON ──────────────────────────────────────────────────────────────
  // Zielony, trochę więcej particles. Bez shake jeszcze.
  uncommon: {
    primaryColor: 0x4ADE80,             // jasny zielony (matched z RARITY_FX HatchAnim)
    secondaryColor: 0xBBF7D0,
    particleTextureKey: 'fx-particle-grey',  // używamy grey + tint w runtime — mniej textur
    particleCount: 12,
    flashAlpha: 0.78,
    flashRadius: 200,
    shockwaveRadius: 180,
    shockwaveLineWidth: 5,
    shockwaveRingCount: 1,
    cameraShakeIntensity: 0,
    cameraShakeMs: 0,
    durationMult: 1.05,
  },

  // ── RARE ──────────────────────────────────────────────────────────────────
  // Niebieski, wyraźny shockwave + lekki shake (gracz czuje "uuu, rare").
  rare: {
    primaryColor: 0x60A5FA,             // jasny niebieski
    secondaryColor: 0xBAE6FD,
    particleTextureKey: 'fx-particle-cyan',
    particleCount: 18,
    flashAlpha: 0.85,
    flashRadius: 220,
    shockwaveRadius: 210,
    shockwaveLineWidth: 5,
    shockwaveRingCount: 1,
    cameraShakeIntensity: 0.004,
    cameraShakeMs: 150,
    durationMult: 1.1,
  },

  // ── EPIC ──────────────────────────────────────────────────────────────────
  // Fiolet, więcej particles, mocniejszy flash.
  epic: {
    primaryColor: 0xC084FC,             // fiolet
    secondaryColor: 0xF0ABFC,
    particleTextureKey: 'fx-particle-cyan',  // cyan jako secondary — kontrastuje z fioletem
    particleCount: 24,
    flashAlpha: 0.9,
    flashRadius: 250,
    shockwaveRadius: 240,
    shockwaveLineWidth: 6,
    shockwaveRingCount: 1,
    cameraShakeIntensity: 0.007,
    cameraShakeMs: 220,
    durationMult: 1.15,
  },

  // ── LEGENDARY ─────────────────────────────────────────────────────────────
  // Złoto/pomarańcz, 2 ringi shockwave, dużo particles, mocny shake.
  // "Wow moment" — gracz widzi że to coś specjalnego nim badge się załaduje.
  legendary: {
    primaryColor: 0xFACC15,             // ciepłe złoto
    secondaryColor: 0xFB923C,           // ciepły pomarańcz
    particleTextureKey: 'fx-particle-yellow',
    particleCount: 32,
    flashAlpha: 0.95,
    flashRadius: 290,
    shockwaveRadius: 280,
    shockwaveLineWidth: 7,
    shockwaveRingCount: 2,              // double ring = legendary feel
    cameraShakeIntensity: 0.012,
    cameraShakeMs: 350,
    durationMult: 1.2,
  },

  // ── MYTHIC ────────────────────────────────────────────────────────────────
  // Czerwono-złoty (Codex 23d: nie pure red — to byłby damage vibe). Tutaj
  // czerwony zmiksowany z różowo-złotym sparkly accent. Max particles, dwa
  // ringi, strong shake.
  mythic: {
    primaryColor: 0xF87171,             // jasny czerwony (różowawy, nie damage'owy)
    secondaryColor: 0xFFD24A,           // złoty akcent
    particleTextureKey: 'fx-particle-yellow',
    particleCount: 40,
    flashAlpha: 1.0,
    flashRadius: 320,
    shockwaveRadius: 310,
    shockwaveLineWidth: 7,
    shockwaveRingCount: 2,
    cameraShakeIntensity: 0.015,
    cameraShakeMs: 400,
    durationMult: 1.25,
  },
};

/**
 * HUGE override — niezależny od rarity. Aplikowany gdy `payload.isHuge === true`
 * LUB `payload.variant === 'huge'`. Codex 23d: huge w Pet Sim style to MASA
 * (większy pet), nie wariant koloru. Stąd biało-złoty glow, NIE czerwony.
 */
export const HUGE_HATCH_PRESET: HatchRarityPreset = {
  primaryColor: 0xFFF7AD,                // bardzo jasny ciepły żółty
  secondaryColor: 0xFFFFFF,
  particleTextureKey: 'fx-particle-yellow',
  particleCount: 35,
  flashAlpha: 1.0,
  flashRadius: 340,
  shockwaveRadius: 330,
  shockwaveLineWidth: 8,
  shockwaveRingCount: 2,
  cameraShakeIntensity: 0.014,
  cameraShakeMs: 420,
  durationMult: 1.3,
};

/**
 * TITANIC override — apex rozmiarowy ponad huge. Jeszcze intensywniejszy:
 * kosmiczny fiolet/biel, więcej cząstek, mocniejszy shake, dłuższy reveal.
 * Aplikowany gdy `variant === 'titanic'` (priorytet nad HUGE i rarity).
 */
export const TITANIC_HATCH_PRESET: HatchRarityPreset = {
  primaryColor: 0xCBB0FF,                // kosmiczny fiolet
  secondaryColor: 0xFFFFFF,
  particleTextureKey: 'fx-particle-white',
  particleCount: 48,
  flashAlpha: 1.0,
  flashRadius: 380,
  shockwaveRadius: 380,
  shockwaveLineWidth: 9,
  shockwaveRingCount: 3,
  cameraShakeIntensity: 0.018,
  cameraShakeMs: 480,
  durationMult: 1.45,
};

/**
 * Resolver: na podstawie rarity + variant zwraca odpowiedni preset.
 * Priorytet override'ów (od najwyższego): TITANIC → HUGE → rarity preset.
 * Niezależne od rarity — wariant rozmiarowy decyduje o "feel" cinematicu.
 */
export function resolveHatchPreset(rarity: Rarity, variant: Variant): HatchRarityPreset {
  if (variant === 'titanic') return TITANIC_HATCH_PRESET;
  if (variant === 'huge') return HUGE_HATCH_PRESET;
  return HATCH_RARITY_PRESETS[rarity];
}

// ═══════════════════════════════════════════════════════════════════════════
// BULK HATCH PRESET (5.D.3c — DESIGN_BULK_HATCH §"Mockup animacji")
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Layout gridu reveal phase wg liczby petów. Per design doc tabela §1.2:
 *  - 2     → 1 rząd × 2 (BIG)
 *  - 3-4   → 2 rzędy × 2 (BIG)
 *  - 5-6   → 2 rzędy × 3 (MID)
 *  - 7-8   → 2 rzędy × 4 (MID)
 *  - 9-10  → 2 rzędy × 5 (MID)
 *
 * 11-12 (przyszły bulk_hatch upgrade): 3×4 — design doc ma 12 jako max.
 * 5.D.3b backend wspiera max 10, więc 11-12 to forward-compat.
 */
export function bulkGridLayout(n: number): { cols: number; rows: number; isBig: boolean } {
  if (n <= 2) return { cols: 2, rows: 1, isBig: true };
  if (n <= 4) return { cols: 2, rows: 2, isBig: true };
  if (n <= 6) return { cols: 3, rows: 2, isBig: false };
  if (n <= 8) return { cols: 4, rows: 2, isBig: false };
  if (n <= 10) return { cols: 5, rows: 2, isBig: false };
  return { cols: 4, rows: 3, isBig: false };   // 11-12 forward-compat
}

/**
 * Bulk-mode preset — czasy faz + stagger + globalne particle counts.
 * Per design doc fazy 1-5: intro 0.4s / charge 0.6s / burst 0.3s /
 * reveal 0.8s base (+ stagger N×90ms) / stand-by (open until dismiss).
 */
export const HATCH_BULK_PRESET = {
  /** Phase durations (ms). */
  introMs: 400,
  chargeMs: 600,
  burstMs: 300,
  /** Reveal base — łączny czas wjazdu wszystkich petów = revealBaseMs + (N-1) × revealStaggerMs. */
  revealBaseMs: 800,
  revealStaggerMs: 90,

  /** Skip dostępny po tym czasie od startu. */
  skipAvailableAfterMs: 500,

  // ── Faza 1: intro ─────────────────────────────────────────────────────────
  /** Dim alpha końcowy (Phase 1). */
  introDimAlpha: 0.55,
  /** Egg "stack" (piramida): rzędy w stosie. Każdy rząd = 2-3 jajka. */
  introEggStackRows: 3,
  /** Skala każdego jajka w stosie. */
  introEggScale: 0.18,
  /** Liczba subtle "gold dust" particles per second w fazie 1. */
  introDustParticleRate: 6,

  // ── Faza 2: charge ────────────────────────────────────────────────────────
  /** Liczba pulsów stosu jajek (1.0 → 1.15 → 1.0). */
  chargePulses: 3,
  /** Shockwave ring radius (px) wokół stosu. */
  chargeShockwaveRadius: 200,
  chargeShockwaveLineWidth: 5,
  /** Subtle camera shake intensity (relatywne do max wymiaru viewport). */
  chargeShakeIntensity: 0.003,

  // ── Faza 3: burst ─────────────────────────────────────────────────────────
  /** Full-screen white flash alpha peak. */
  burstFlashAlpha: 0.95,
  /** Liczba rainbow confetti particles. */
  burstConfettiCount: 50,
  /** Shockwave ring max radius (px). */
  burstShockwaveRadius: 480,
  burstShockwaveLineWidth: 8,
  /** Camera punch intensity. */
  burstShakeIntensity: 0.012,
  burstShakeMs: 280,

  // ── Faza 4: reveal grid ───────────────────────────────────────────────────
  /** Display size peta jako % min wymiaru viewportu (BIG layout — 2/4 petów). */
  petBigDisplayPct: 0.22,
  /** Display size peta jako % min wymiaru viewportu (MID layout — 6/8/10 petów). */
  petMidDisplayPct: 0.13,
  /** Bounce peak skali (scale * peak → scale). */
  bouncePeakMult: 1.25,
  /** Stagger między rarity tiers — common najpierw, huge na końcu. */
  rarityOrder: ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'] as const,

  // ── Faza 5: stand-by ──────────────────────────────────────────────────────
  // Faza 5 to React overlay (grid PetCard z liczbami + dismiss button).
  // Phaser kończy się po fazie 4 — wszystkie tweens cleanowane przez destroy().
} as const;

/**
 * Łączny czas faz 1-4 (bez stand-by — to robi React overlay po `hatch:complete`).
 * Używane przez HatchBulkDirector do triggera końca cinematic.
 *
 * Faza 4 to revealBaseMs (bounce ostatniego peta) + (N-1) × staggerMs (delay
 * startu ostatniego peta). +200ms buffer żeby ostatni bounce dobiegł do końca
 * zanim destroy() wyłączy pet sprite'y.
 */
export function bulkPhasesTotalMs(petCount: number): number {
  const reveal = HATCH_BULK_PRESET.revealBaseMs
    + Math.max(0, petCount - 1) * HATCH_BULK_PRESET.revealStaggerMs;
  return HATCH_BULK_PRESET.introMs + HATCH_BULK_PRESET.chargeMs
    + HATCH_BULK_PRESET.burstMs + reveal + 200;
}

/** Indeks rarity dla sortowania reveal (mniej = wcześniej w stagger). */
export function rarityOrderIndex(rarity: Rarity): number {
  const i = HATCH_BULK_PRESET.rarityOrder.indexOf(rarity);
  return i < 0 ? 0 : i;
}
